/**
 * Pure decision logic for AgentTeamPanel's roster cards and detail panel — resolving the
 * current inventory scope down to a building-id set, picking each agent's most recent run
 * within that scope, and turning that into the card's status text state. Split out from
 * AgentTeamPanel.tsx so that file exports only components (react-refresh/only-export-components)
 * and so this logic is directly unit-testable without a DOM (story #1586 — every scope level
 * needs a deliberate status, never a blank/error tile).
 */
import type { AgentDefinition, AgentRun } from "@/lib/api/agent";
import type { NormalizedActivityRow } from "@/components/control-tower/activity-feed";
import type { PerspectivePath } from "@/lib/perspectives";
import type { CardTone } from "@/lib/card-styles";

// Salkku (group) vs a single building — the two scoped (non-inventory-wide) levels a card's
// status can be narrowed to. Kiinteistökanta (the whole inventory) is `null` — no narrowing.
export type ScopeLabel = "salkussa" | "kiinteistössä";

/** The building-id set the current inventory scope resolves to. `null` means "no narrowing" —
 *  kiinteistökanta scope, where an agent's own `last_run_at` is already the right aggregate. */
export function scope_building_ids(
  buildingId: string | null,
  activeGroupBuildingIds: string[] | null,
): string[] | null {
  if (buildingId) return [buildingId];
  if (activeGroupBuildingIds) return activeGroupBuildingIds;
  return null;
}

/** Most recent run (any status) whose `building_ids` intersects the scope, or `null` if none do. */
export function latest_run_in_scope(
  runs: AgentRun[],
  scopeBuildingIds: string[],
): AgentRun | null {
  const scoped = runs.filter((r) =>
    r.building_ids.some((id) => scopeBuildingIds.includes(id)),
  );
  if (scoped.length === 0) return null;
  return [...scoped].sort((a, b) =>
    b.created_at.localeCompare(a.created_at),
  )[0];
}

export type AgentScopeStatus =
  // Runs for a scoped (salkku/building) card are still in flight — never render this as blank.
  | { kind: "loading" }
  // A last-run timestamp to show, either the agent's own aggregate (kiinteistökanta) or the
  // latest run found within the scope (salkku/building). `null` = never run at all.
  | { kind: "aggregate"; lastRunAt: string | null }
  // Scoped (salkku/building) and nothing in `runs` touches this scope — the deliberate weaker
  // fleet/empty state called for instead of reusing the (misleading, out-of-scope) global value.
  | { kind: "empty"; scopeLabel: ScopeLabel };

/** Newest Tapahtumaloki `when` this agent produced in the already-scoped feed, or `null`.
 *  Rows are `when`-descending from `loadActivityFeed`. */
export function latest_activity_when(
  rows: NormalizedActivityRow[],
  agentDisplayName: string,
): string | null {
  return filter_activity_for_agent(rows, agentDisplayName)[0]?.when ?? null;
}

/** The single status decision a roster card (and its detail panel's fallback) reads — see the
 *  `AgentScopeStatus` variants above for what each scope level renders.
 *
 *  `lastActivityAt` is preferred when present: Tapahtumaloki is produced work in this scope
 *  (findings from the 15-min KB tick, recipes, …). AgentRun.building_ids is only a fleet-cycle
 *  stamp and is often empty for Datavahtimestari / Konfiguraatiotarkastaja, whose scheduled
 *  work never writes an AgentRun. Pass `undefined` while the feed is still loading so a
 *  scoped card stays on `loading` rather than flashing the empty state. */
export function agent_scope_status(
  agent: AgentDefinition,
  scopeBuildingIds: string[] | null,
  scopedRuns: AgentRun[] | undefined,
  scopeLabel: ScopeLabel,
  lastActivityAt?: string | null,
): AgentScopeStatus {
  if (lastActivityAt) {
    return { kind: "aggregate", lastRunAt: lastActivityAt };
  }
  if (scopeBuildingIds === null) {
    return { kind: "aggregate", lastRunAt: agent.last_run_at };
  }
  if (scopedRuns === undefined) return { kind: "loading" };
  const latest = latest_run_in_scope(scopedRuns, scopeBuildingIds);
  return latest
    ? { kind: "aggregate", lastRunAt: latest.created_at }
    : { kind: "empty", scopeLabel };
}

/** Tapahtumaloki rows this agent itself produced (§2.2 `who.kind === "agent"`), already scoped
 *  by the caller's `loadActivityFeed(scope, …)` — the detail panel's primary body.
 *
 *  Filters client-side rather than adding an actor param to `ListActivityOptions`
 *  (lib/api/activity.ts) — a server-side filter would be the righter depth (it already has
 *  `categories`/`kinds`), but that's an `adapters/http/` contract change this `scope:frontend`
 *  story isn't authorized to make (no `skills: api`). Row counts per building are small enough
 *  that the client-side filter is not a real cost today. */
export function filter_activity_for_agent(
  rows: NormalizedActivityRow[],
  agentDisplayName: string,
): NormalizedActivityRow[] {
  return rows.filter(
    (row) => row.who.kind === "agent" && row.who.label === agentDisplayName,
  );
}

/** Where this scheduled agent's produced work lives — keyed by agent id, not `icon_type`
 *  (Toimintatarkastaja and Konfiguraatiotarkastaja both use `config`). Lives on the roster
 *  card so Tapahtumaloki stays a thin action log. */
export type ProducedWorkNav = { label: string; route: PerspectivePath };

const PRODUCED_WORK_NAV: Record<string, ProducedWorkNav> = {
  "data-quality-inspector-v1": {
    label: "Avaa Datan laatu",
    route: "/data-quality",
  },
  "behavior-evaluator-v1": {
    label: "Avaa Talotekniikan toimivuus",
    route: "/equipment-performance",
  },
  "configuration-inspector-v1": {
    label: "Avaa Konfiguraatio",
    route: "/configuration",
  },
  "energy-optimizer-v1": {
    label: "Avaa Optimointi",
    route: "/energy-efficiency",
  },
  "strategic-recommender-v1": {
    label: "Avaa Strategia",
    route: "/savings-potential",
  },
  "simulation-agent-v1": {
    label: "Avaa Säästöpotentiaali",
    route: "/savings-potential",
  },
};

export function produced_work_nav(agentId: string): ProducedWorkNav | null {
  return PRODUCED_WORK_NAV[agentId] ?? null;
}

// ── Per-building schedule pause / scoped trigger (story #1604) ─────────────────────────────────
//
// Play/Pause and "Aja nyt" only make sense once a building is selected — `POST /agents/{id}/run`
// and the persisted `(agent_id, building_id)` schedule row both require one (tasks #1606/#1607).
// At kiinteistökanta/salkku scope (`buildingId === null`) neither control renders at all — never
// a fleet-wide pause, never an ambiguous fleet-wide trigger.

/** Whether the schedule Play/Pause control is shown at all, and — once a building is selected —
 *  the server's persisted enabled/paused value, or `loading` while a fresh `listAgents(buildingId)`
 *  read is still in flight (never fabricated from a stale/no-building read). */
export type ScheduleControlState =
  | { kind: "hidden" }
  | { kind: "loading" }
  | { kind: "resolved"; enabled: boolean };

export function schedule_control_state(
  buildingId: string | null,
  enabledForBuilding: boolean | null,
): ScheduleControlState {
  if (buildingId === null) return { kind: "hidden" };
  if (enabledForBuilding === null) return { kind: "loading" };
  return { kind: "resolved", enabled: enabledForBuilding };
}

/** The schedule toggle's status label — paused vs running, never inferred from the trigger's
 *  own run_state (that's a one-shot "Aja nyt" outcome, a separate concept — see the Done-when
 *  "not conflated with the paused state"). */
export function schedule_status_label(state: ScheduleControlState): string {
  if (state.kind !== "resolved") return "…";
  return state.enabled ? "Käynnissä" : "Pysäytetty";
}

/** Semantic tile tone for the schedule state — paused is `warn` (intentional off), never
 *  `empty` (too quiet to scan) and never destructive (not a fault). Hidden/loading stay
 *  `empty` so kanta/salkku cards do not pretend they have a per-building schedule. */
export function schedule_card_tone(state: ScheduleControlState): CardTone {
  if (state.kind !== "resolved") return "empty";
  return state.enabled ? "ok" : "warn";
}

/** "Aja nyt" (the one-shot manual trigger) is visible under the same rule as the schedule
 *  control — both need a selected building — but it stays enabled while paused: triggering must
 *  work whether or not the schedule is on (task #1607), so it never reads `enabled`. */
export function trigger_visible(buildingId: string | null): boolean {
  return buildingId !== null;
}
